<script setup>
import { useGesture } from '@/composables/calendar/useGesture';
import { DEV_MODE } from '@/config/dev-mode';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import axios from 'axios';

import { fetchBaseServiceAPI } from '@/api/baseServiceApi';
import { postDailyFortuneRateAPI, getDailyFortuneRateAPI } from '@/api/calendarApi';
import { getTicketCountAPI, getAnalysisLogAPI } from '@/api/AnalysisApi';
import { useAnalysisServices } from '@/composables/saju-insight/useAnalysisServices';
import { formatConsoleMessage, isObjectEmpty, padNumber } from '@/composables/functions';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePaymentsStore } from '@/store/modules/payments';
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useMobileSajuLnbStore } from '@/store/modules/mobileSajuLnb';
import {
  MOBILE_YONGSIN_CONTENTS,
  MOBILE_YONGSIN_SECTION_PAGE_MAP,
} from '@/pages/mobile/analysis-saju-youngsin/constants';
import { yongsinComponentMap } from '@/pages/mobile/analysis-saju-youngsin/summaryComponentMap';

import overviewSajuSummary01 from '@/pages/mobile/analysis-saju-youngsin/_summary_01.vue';
import overviewSajuSummary02 from '@/pages/mobile/analysis-saju-youngsin/_summary_02.vue';
import PopDesc from '@/pages/mobile/analysis-saju-youngsin/_pop_desc.vue';
import SurveyCard from '@/components/calendar/drawer/pages/cards/SurveyCard.vue';

import PopAnalysisInfo from '@/components/saju-insight/common/PopAnalysisInfo.vue';
import PopAnalysisInfoOther from '@/components/saju-insight/common/PopAnalysisInfoOther.vue';
import AnalysisLoadingOverlay from '@/components/saju-insight/common/AnalysisLoadingOverlay.vue';
import IconClose01 from '@/components/svg/IconClose01.vue';
import IconList from '@/components/svg/IconList.vue';
import { useSajuData } from '@/composables/saju-insight/useSajuData';

definePage({ meta: { layout: 'empty' } });

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const accessToken = ref(null);
const analysisStore = useAnalysisStore();
const analysisDataStore = useAnalysisDataStore();
const paymentsStore = usePaymentsStore();
const mobileSajuLnbStore = useMobileSajuLnbStore();
const { serviceData, serviceContents, fetchContentByKey } = useAnalysisServices();

const { sajuData, yData, pSubData, baseCode, transdata } = storeToRefs(analysisDataStore);

// =============== PC 테스트 환경 ===============
const store = useStore();
const userId = ref(null);
userId.value = store.getters['user/id'];

if (userId.value) {
  // PC 버전에서 userId가 있으면 로그인 상태로 판단
  accessToken.value = true;
}

// =============== 웹뷰 통신 ===============
/** 모바일 사용자 정보 */
// const accessToken = ref(null);
const authUserId = ref(null);
const guestUserId = ref(null);
const bottomInset = ref(0);
const isInitializing = ref(true);

/** 앱으로 메시지 전송 */
const sendMessageToApp = ({ action, message, route }) => {
  if (window.ReactNativeWebView) {
    const messageData = {
      action,
      payload: {
        message,
        route,
      },
    };
    window.ReactNativeWebView.postMessage(JSON.stringify(messageData));
  }
};

const debuggingHandler = ({ action, message }) => {
  if (DEV_MODE) {
    switch (action) {
      case 'debug':
        console.log(formatConsoleMessage(message));
        break;
      case 'error':
        console.error(formatConsoleMessage(message));
        break;
      default:
        console.warn(`Unknown debug action: ${action}`);
        break;
    }
  }

  sendMessageToApp({ action, message });
};

/** 모바일 앱과 통신 */
const handleMessage = async (event) => {
  isInitializing.value = true;
  try {
    let data;

    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else {
      data = event.data;
    }

    if (data.source === 'react-devtools-content-script') return;
    if (data.key === '__VUE_DEVTOOLS_VUE_DETECTED_EVENT__') return;

    // if (data.action === 'refresh') {
    //   sendMessageToApp({ action: 'close' })
    // }

    // message 수신되었을 때
    if (data.action === 'init') {
      accessToken.value = data?.payload?.accessToken;
      authUserId.value = data?.payload?.authUserId;
      mobileSajuLnbStore.setGuestUserId(data?.payload?.guestUserId);
      mobileSajuLnbStore.setUserName(data?.payload?.userName);
      mobileSajuLnbStore.setBottomInset(data?.payload?.bottom);

      axios.defaults.headers.common['Authorization'] = accessToken?.value;
      axios.defaults.headers.common['authUserId'] = authUserId?.value;
      sendMessageToApp({ action: 'debug', message: `${JSON.stringify(accessToken.value)}` });
    }
    if (data.action === 'reset') {
      resetPopup();
    }
    if (data.action === 'refresh') {
      if (guestUserId.value && !isObjectEmpty(sajuData.value)) {
        refreshPersonalityPageData();
      }
    }
  } catch (e) {
    console.error('Invalid message received:', `${e}`);
  } finally {
    isInitializing.value = false;
  }
};

// =============== 사주 데이터 ===============
const { getSajuParams, formatSajuParams, getSajuData } = useSajuData();

const isLoadingSajuData = ref(false);
/** 사주 데이터 초기화 및 파생값 계산 */
const initializeSajuData = async () => {
  isLoadingSajuData.value = true;
  try {
    const sajuParams = await getSajuParams(guestUserId.value);
    if (!sajuParams) return;

    const formattedSajuParams = formatSajuParams(sajuParams, analysisDataStore.paramData);
    const sajuResult = await getSajuData(formattedSajuParams);
    if (!sajuResult) return;

    analysisStore.setUserName(sajuResult?.inputdata?.name);
    analysisDataStore.setSajuData(sajuResult);
  } catch (e) {
    debuggingHandler({ action: 'error', message: `상세분석 > 사주정보 요청 실패: ${e}` });
  } finally {
    isLoadingSajuData.value = false;
  }
};

// =============== 1. 이용중인 용신분석 정보 ===============

/** 이용중인 용신분석 정보 요청 중 상태 */
const isLoadingAnalysisInfo = ref(false);

/** 이용중인 용신분석 정보 요청 완료 여부 */
const hasLoadedAnalysisInfo = ref(false);

/** 이용중인 용신분석 정보 요청 */
const getAnalysisInfo = async () => {
  isLoadingAnalysisInfo.value = true;
  hasLoadedAnalysisInfo.value = false;

  try {
    await fetchContentByKey('yongsin');
    hasLoadedAnalysisInfo.value = true;
  } catch (e) {
    debuggingHandler({
      action: 'error',
      message: `성향분석 > 1. 이용중인 성향분석 정보 요청 실패: ${e}`,
    });
  } finally {
    isLoadingAnalysisInfo.value = false;
  }
};

// =============== 2. 성향분석 데이터 ===============
/** 성향분석 데이터 요청 중 상태 */
const isFetchingPersonality = ref(false);

/** 성향분석 데이터 요청 완료 여부 */
const hasFetchedPersonality = ref(false);

/** 성향분석 데이터 요청 params */
const personalityParams = computed(() => {
  if (!sajuData.value || isObjectEmpty(sajuData.value)) return null;

  return {
    guestUserId: guestUserId.value,
    lang: 'KOR',
    birth: sajuData.value.solar ?? null,
    userName: sajuData.value.inputdata.name ?? '',
    bln: sajuData.value.transdata.lucknum ?? null,
    gender: Number(sajuData.value.inputdata.gender) ?? null,
    unknownTime: sajuData.value.inputdata.unknown_time ?? null,
    analysisTypeId: 4,
  };
});

/** 성향분석 기본 데이터 요청 */
const fetchSubData = async () => {
  hasFetchedPersonality.value = false;
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.pSubData)) {
      const subDataParams = {
        ...personalityParams.value,
        analysisTypeId: 4,
      };

      const result = await fetchBaseServiceAPI(subDataParams);

      if (result?.data?.성향종합분석) {
        analysisDataStore.setPSubData(result?.data?.성향종합분석);
        analysisDataStore.setYData({});

        hasFetchedPersonality.value = true;
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 2-1. 기본 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingPersonality.value = false;
  }
};

/** 내 성향분석 데이터 요청 */
const fetchPersonalityData = async () => {
  hasFetchedPersonality.value = false;
  isFetchingPersonality.value = true;
  try {
    if (isObjectEmpty(analysisDataStore.yData)) {
      const result = await fetchBaseServiceAPI(personalityParams.value);

      if (result?.data) {
        analysisDataStore.setYData(result?.data);
        analysisDataStore.setPSubData({});

        hasFetchedPersonality.value = true;

        analysisStore.setAnalysisResult(result?.data);
      }
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 2-2. 분석권 데이터 요청 실패: ${e}` });
  } finally {
    isFetchingPersonality.value = false;
  }
};

/** 성향분석 데이터 초기화 */
const initPersonalityData = async () => {
  if (!serviceData.yongsin.permission.value) {
    await fetchSubData();
  } else {
    await fetchPersonalityData();
  }
};

/** 성향분석 페이지 데이터 로딩 상태 */
const isRefreshingPageData = ref(false);

/** 성향분석 페이지 데이터 새로고침 */
const refreshPersonalityPageData = async () => {
  isRefreshingPageData.value = true;
  try {
    await Promise.all([
      getAnalysisInfo(),
      analysisStore.getAnalysisLog(guestUserId.value, 4), // 용신분석 typeId: 4
      paymentsStore.fetchTicketCount(),
      paymentsStore.getOwnPoint(),
    ]);
    await initPersonalityData();
  } catch (e) {
    debuggingHandler({ action: 'error', message: `용신분석 > 페이지 새로고침 실패: ${e}` });
  } finally {
    isRefreshingPageData.value = false;
  }
};

// =============== 3. 컨텐츠 평가 ===============
/** 컨텐츠 평가 여부 */
const ratingStatus = ref(false);

/** 컨텐츠 평가 점수 */
const ratingResult = ref(0);

/** 컨텐츠 평가 조회하기 */
const getSajuPersonalityRate = async () => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      serviceName: 'YONGSIN_ANALYSIS',
      objectKey,
    };
    const result = await getDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      ratingResult.value = result.score;
    }
  } catch (e) {
    if (e?.response?.status === 404) {
      debuggingHandler({
        action: 'error',
        message: `성향분석 > 3-1. 평가 조회 실패 status: ${e?.response?.status}`,
      });
      ratingStatus.value = false;
      ratingResult.value = 0;
    }
  }
};

/** 컨텐츠 평가하기 */
const postSajuPersonalityRate = async (score) => {
  const objectKey = `${guestUserId.value}`;

  try {
    const params = {
      score,
      serviceName: 'YONGSIN_ANALYSIS',
      objectKey,
    };
    const result = await postDailyFortuneRateAPI(params);
    if (result) {
      ratingStatus.value = true;
      getSajuPersonalityRate();
    }
  } catch (e) {
    debuggingHandler({ action: 'error', message: `성향분석 > 3-2. 평가하기 실패: ${e}` });
  }
};

const showPopAnalysisInfoDo = async () => {
  if (isRefreshingPageData.value) return;

  // 용신분석권 사용중일 때,
  if (serviceData.yongsin.permission.value) {
    showPopAnalysisInfo.value = false;
    showPopAnalysisInfoOther.value = false;
    return;
  }

  if (
    paymentsStore?.points < serviceContents.yongsin.value?.pointPrice &&
    serviceData.yongsin.ticketCount.value === 0
  ) {
    // 포인트가 적고, 분석권도 없을 때
    // 포인트 충전 팝업 표시 (분석권 구매 팝업 X)
    showPopAnalysisInfoOther.value = false;
    showPopAnalysisInfo.value = true;
  } else {
    // 포인트 또는 분석권이 충분할 때,
    // 분석권 구매 팝업 표시 (포인트 충전 팝업 X)
    showPopAnalysisInfoOther.value = true;
    showPopAnalysisInfo.value = false;
  }
};

watch(
  () => mobileSajuLnbStore.guestUserId,
  (newId) => {
    if (newId) guestUserId.value = newId;
  },
);

watch(guestUserId, async (newId, oldId) => {
  if (newId && newId !== oldId) {
    if (!isObjectEmpty(sajuData.value)) {
      await refreshPersonalityPageData();
      await getSajuPersonalityRate();
    } else {
      await initializeSajuData();
      await refreshPersonalityPageData();
      await getSajuPersonalityRate();
    }
  }
});

// =============== 하단 탭 영역 (직업추천 & 용어해석) ===============
const showPopDesc = ref(false);
const openPopDesc = () => {
  if (isRefreshingPageData.value) return;
  if (!serviceData.yongsin.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  showPopDesc.value = true;
};
const closePopDesc = () => {
  showPopDesc.value = false;
};

// =============== 페이지 이동 ===============
const currentPage = ref(0); // 현재 페이지
const totalPages = 20; // 전체 페이지

const showSummary02 = ref(false); // overviewSajuSummary02 표시 여부

const showPopAnalysisInfo = ref(false);
const showPopAnalysisInfoOther = ref(false);

const goToCoverPage = () => {
  currentPage.value = 0;
  showSummary02.value = false;
};

const clickPrevPage = () => {
  if (currentPage.value < 1) return;
  else currentPage.value -= 1;
};

const clickNextPage = () => {
  if (currentPage.value === 2 && !serviceData.yongsin.permission.value) {
    showPopAnalysisInfoDo();
    return;
  }
  if (currentPage.value >= totalPages) goToCoverPage();
  else currentPage.value += 1;
};

/** 통합 제스처 핸들러 */
const { handleStart, handleMove, handleEnd } = useGesture({
  onSwipeLeft: clickNextPage, // 왼쪽 스와이프 → 다음 페이지 이동
  onSwipeRight: clickPrevPage, // 오른쪽 스와이프 → 이전 페이지 이동
  SWIPE_THRESHOLD: 150,
});

const handleNavigateToPage = (tocIdx) => {
  debuggingHandler({ action: 'debug', message: `용신분석 > 목차 이동: ${tocIdx}` });

  const mappedPage = MOBILE_YONGSIN_SECTION_PAGE_MAP[tocIdx];

  if (mappedPage !== undefined) {
    currentPage.value = mappedPage;
  } else {
    debuggingHandler({
      action: 'error',
      message: `용신분석 > 매핑되지 않은 목차 인덱스: ${tocIdx}`,
    });
  }
};

/** 팝업 닫기 */
const close = () => {
  if (window.ReactNativeWebView) {
    router.push('/mobile/saju-lnb');
  } else {
    router.back();
  }
};

const resetPopup = () => {
  currentPage.value = 0;
  showSummary02.value = false;
  showPopDesc.value = false;
};

const onClickRecharge = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    sendMessageToApp({
      action: 'route',
      route: {
        screen: 'points',
        shortageAmount,
      },
    });
  }
};

const handelShowSummary02Open = () => {
  showSummary02.value = true;
};

// 페이지별 컴포넌트 반환 함수
const getComponentByPage = (page) => {
  return yongsinComponentMap[page] || null;
};

// 페이지별 CSS 클래스 반환 함수
const getComponentClass = (page) => {
  return page % 2 === 1 ? 'mobile-content-first' : 'mobile-content-second';
};

const openFlag = ref('');
const openContentsList = () => {
  if (openFlag.value === '') openFlag.value = 'open';
  else openFlag.value = '';
};
const closeContentsList = () => (openFlag.value = '');

/** index값으로 페이지 이동 */
const updateIndexNo = (id) => {
  if (id < 2) {
    // props.updateSelectedDescNav('')
  } else {
    if (id > 2 && !serviceData.yongsin.permission.value) {
      // emit('goPopAnalysis')
      showPopAnalysisInfoDo();
      return;
    }
    // props.updateSelectedDescNav('personalityJob')
  }
  currentPage.value = id;
  closeContentsList();
};

// 인덱스
// const indexNo = ref(1)
watch(currentPage, (newPage) => {
  analysisStore.setYongsinIndex(newPage);
  // currentPage.value = newIndex
});

watch(
  () => analysisStore.selectedContentIndex,
  (newIdx) => {
    // const target = MOBILE_YONGSIN_CONTENTS[newIdx]
    // if (target?.id) updateIndexNo(target.id)
    const target = MOBILE_YONGSIN_SECTION_PAGE_MAP[newIdx];
    if (target) updateIndexNo(target);
  },
  { immediate: true },
);

const handleClosePop = () => {
  showPopAnalysisInfo.value = false;
};

const handleChargePop = (shortageAmount) => {
  if (window.ReactNativeWebView) {
    onClickRecharge(shortageAmount);
  } else {
    const path = '/purchase/points';
    router.push(path);
  }
  showPopAnalysisInfo.value = false;
};

const handleClosePopOther = () => {
  showPopAnalysisInfoOther.value = false;
};

const handleChargePopOtherProduct = () => {
  if (window.ReactNativeWebView) {
    onClickRecharge();
  } else {
    router.push('/purchase/tickets');
  }
  showPopAnalysisInfoOther.value = false;
};

/** 성향분석 결제 성공 후 실행 */
const paymentSuccessDo = async (success) => {
  if (success) {
    try {
      await refreshPersonalityPageData();
      await getSajuPersonalityRate();
      mobileSajuLnbStore.setNeedRefresh(true);
    } catch (e) {
      debuggingHandler({ action: 'error', message: `성향분석권 결제 후 새로고침 중 오류: ${e}` });
    }
  }
};

onMounted(async () => {
  window.addEventListener('message', handleMessage);
  document.addEventListener('message', handleMessage);

  if (guestUserId.value) getSajuPersonalityRate();

  await nextTick();

  if (window.ReactNativeWebView) {
    if (mobileSajuLnbStore.guestUserId) {
      guestUserId.value = mobileSajuLnbStore.guestUserId;
    }
  } else {
    //REVIEW: 테스트 중
    guestUserId.value = 24918;
  }
});

onUnmounted(() => {
  window.removeEventListener('message', handleMessage);
  document.removeEventListener('message', handleMessage);

  analysisStore.setYongsinIndex(1);
  analysisStore.setSelectedContentIndex(null);
});
</script>

<template>
  <!-- 분석권 및 포인트 부족 -->
  <PopAnalysisInfo
    :isVisible="showPopAnalysisInfo"
    :analysisType="serviceContents.yongsin.value"
    :ticketCountData="serviceData.yongsin.ticketData.value"
    :ticketCount="serviceData.yongsin.ticketCount.value"
    @close="handleClosePop"
    @charge="handleChargePop"
  />
  <!-- 분석권 또는 포인트 사용 가능 -->
  <PopAnalysisInfoOther
    :isVisible="showPopAnalysisInfoOther"
    :analysisType="serviceContents.yongsin.value"
    :paymentsStore="paymentsStore"
    :ticketCountData="serviceData.yongsin.ticketData.value"
    :ticketCount="serviceData.yongsin.ticketCount.value"
    @close="handleClosePopOther"
    @charge="handleChargePopOtherProduct"
    @payment-success="paymentSuccessDo"
  />

  <div
    class="pop-saju-reading-container mobile st-analysis-personality"
    @mousedown="handleStart"
    @mousemove="handleMove"
    @mouseup="handleEnd"
    @touchstart="handleStart"
    @touchmove="handleMove"
    @touchend="handleEnd"
  >
    <AnalysisMobileHeader title="낭만 용신분석" :guestUserId="guestUserId" @close="close" />
    <div v-if="currentPage === 0" class="pop-ana-saju-reading-inner intro">
      <overviewSajuSummary01
        v-if="currentPage === 0 && !showSummary02"
        :guestUserId="guestUserId"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :getAnalysisLogDataList="serviceData.yongsin.logData.value"
        :hasPermission="serviceData.yongsin.permission.value"
        :loadingTicketInfo="loadingTicketInfo"
        @handleNavigateToPage="handleNavigateToPage"
        @handelShowSummary02Open="handelShowSummary02Open"
        @recharge="onClickRecharge"
      />
      <AnalysisLoadingOverlay
        :loading="
          currentPage === 0 &&
          showSummary02 &&
          (isInitializing || isLoadingSajuData || isRefreshingPageData)
        "
        :message="'용신 분석중입니다!'"
      />
      <overviewSajuSummary02
        v-if="currentPage === 0 && showSummary02 && !isRefreshingPageData"
        :guestUserName="paramData?.userName || mobileSajuLnbStore?.userName"
        :ticketCount="paymentsStore?.analysisTicketCount || 0"
        :hasPermission="serviceData.yongsin.permission.value"
        :initializing="isInitializing || isLoadingSajuData || isRefreshingPageData"
        @navigateToPage="handleNavigateToPage"
        @updateShowSummary02="
          (value) => {
            console.log('updateShowSummary02 received:', value);
            showSummary02 = value;
          }
        "
        @goPopAnalysis="showPopAnalysisInfoDo"
        @recharge="onClickRecharge"
      />
    </div>

    <div
      v-if="currentPage !== 0"
      class="pop-ana-saju-reading-inner st-page st-content-page st-app-youngsin"
    >
      <div v-if="currentPage >= 1 && currentPage <= 2" class="pop-body mobile">
        <component :is="getComponentByPage(currentPage)" :class="getComponentClass(currentPage)" />
      </div>
      <div v-if="currentPage >= 3 && currentPage <= 18" class="pop-body mobile">
        <component
          :is="getComponentByPage(currentPage)"
          :class="getComponentClass(currentPage)"
          :userName="analysisStore.userName"
          :yData="yData"
          :pSubData="analysisDataStore.pSubData"
          :birthData="sajuData.birthganji"
          :birthJijanggan="sajuData.jijanggan"
          :langMode="sajuData.lang || 'CHNKOR'"
          :birthTitle="sajuData.transdata.title || '시일월년'"
          :transdata="sajuData.transdata"
          :sajuData="sajuData"
          :isActive="sajuData.transdata.isActive"
        />
      </div>
      <div v-if="currentPage === 19" class="pop-body mobile rating">
        <div class="sr__no-content-page yongsin-outro">
          <div class="yongsin-outro__box mobile">
            <div class="nangmane-img" />
            <div class="yongsin-outro__text mobile">
              <div class="yongsin-outro__tit">낭만 용신분석</div>
              <div class="yongsin-outro__desc">
                우리는 사주를 통해 나를 이해하고,<br />
                용신을 통해 균형을 배우며,<br />
                그 위에서 스스로의 길을 만들어갑니다.<br />
                <br />
                용신이라는 개념을 활용하여<br />
                더 지혜로울 수 있는 선택의 길을<br />
                조화롭게 펼쳐가시길 바랍니다<br />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="currentPage === 20" class="pop-body mobile rating">
        <div class="sr__no-content-page">
          <SurveyCard
            style="width: 100%"
            :ratingResult="ratingResult"
            :ratingStatus="ratingStatus"
            @postRate="postSajuPersonalityRate"
          />
        </div>
      </div>

      <div class="contentsListBtnBox mobile">
        <div class="list-box" :class="openFlag">
          <ul class="list-li-box">
            <li
              class="li mobile"
              v-for="item in MOBILE_YONGSIN_CONTENTS.filter((content) => content.title !== '')"
              :key="item.id"
              @click="updateIndexNo(item.id)"
            >
              {{ item.title }}
            </li>
          </ul>
        </div>
      </div>
      <div class="pop-footer-page-box">
        <div class="pop-desc-box">
          <button class="btn-desc" @click="openPopDesc">종합분석 & 용어해석</button>
        </div>
        <div class="pop-pagination" v-if="currentPage !== 0">
          <IconList
            @click="openContentsList('open')"
            v-if="openFlag === ''"
            style="margin-top: 3px"
          />
          <IconClose01
            @click="openContentsList('')"
            v-if="openFlag === 'open'"
            style="margin-top: 3px"
          />
          <button class="pop-pagination__prev" @click="clickPrevPage">
            <i class="icon-chevron"></i>
          </button>
          <div class="pop-pagination__index" @click="openContentsList">
            {{ padNumber(currentPage) }} / {{ totalPages }}
          </div>
          <button class="pop-pagination__next" @click="clickNextPage">
            <i class="icon-chevron right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- 직업추천 & 용어해석 팝업 -->
  <PopDesc
    v-model="showPopDesc"
    :sajuData="sajuData"
    :transdata="transdata"
    :baseCode="baseCode"
    :guestUserId="guestUserId"
    @close="closePopDesc"
  />

</template>

<style lang="scss">
@import '@/assets/nangman_style/popup/saju-reading_mobile.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common.scss';
@import '@/assets/nangman_style/mobile/saju-reading-common-page.scss';
@import '@/assets/nangman_style/_analysis_main_color.scss';

.pop-ana-saju-reading-inner {
  height: calc(100vh - 52px);
  overflow-y: unset;
  &.intro {
    overflow-y: auto;
  }
  &.st-page {
    height: calc(100vh - 104px);
  }
  .btn-footer-box {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    .btn-white {
      display: flex;
      width: 100%;
      height: 48px;
      padding: 14px 20px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      border-radius: 6px;
      border: 1px solid var(--Gray-300, #ebebeb);
      background: var(--gray-100-white, #fff);
      box-shadow: 0px -10px 10px 0px rgba(0, 0, 0, 0.05);
      // color: var(--Gray-800, #333);
      color: $analysis-personality;
      text-align: center;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 20px;
    }
  }
  .pop-body {
    height: auto;
    padding: 0px;
    .v-row {
      margin: 0px !important;
    }
    .personality-wrapper {
      padding: 24px 16px;
    }
    &.rating {
      height: 100%;
    }
  }
}
.st-analysis-personality {
  .pop-ana-saju-reading-inner,
  .overview-cover {
    background: $analysis-personality;
  }
  .pop-ana-saju-reading-inner {
    // padding-bottom: 120px;
  }
}
.st-analysis-personality .pop-ana-saju-reading-inner .pop-body {
  background: #fff;
}

.st-analysis-personality .pop-ana-saju-reading-inner.st-content-page {
  background-color: #fff !important;
  // padding-bottom: 60px !important;
  padding-bottom: 0px;
  overflow-x: hidden !important;
}
</style>
